import { takeLatest, call, put, select } from 'redux-saga/effects';

// Services
import { terminalBlocksApi } from 'services/apiDigitalTicket/terminalBlocks';

// Constants
import { errorMessagersList } from 'helpers/errorMessagers';

// Actions
import * as DialogsActions from 'store/ducks/dialogs/actions';
import * as SnackbarsActions from 'store/ducks/snackbars/actions';
import * as TerminalBlocksActions from './actions';

// Types
import { TerminalBlocksTypes } from './types';

function* list({
  payload,
}: ReturnType<typeof TerminalBlocksActions.listTerminalBlocksRequest>): any {
  try {
    const { body, filtering } = payload;
    if (!filtering) {
      yield put(
        DialogsActions.setOpenLoading({ title: 'Carregando bloqueios' }),
      );
    }
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(terminalBlocksApi.get, {
      params: { transportOperatorId: transportOperator.id, ...body },
    });

    if (response.status === 200) {
      yield put(
        TerminalBlocksActions.listTerminalBlocksSuccess({
          terminalBlocksList: response.data,
          totalCount: parseInt(response.headers.totalcount, 10),
        }),
      );
      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('terminalBlocks - listTerminalBlocks - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(TerminalBlocksActions.listTerminalBlocksFailure());
    yield put(DialogsActions.setClose());
  }
}

function* toggle({
  payload,
}: ReturnType<
  typeof TerminalBlocksActions.toggleBlocksScheduleStatusRequest
>): any {
  try {
    const { blockId } = payload;

    const response = yield call(terminalBlocksApi.toggle, {
      data: { ...payload },
    });

    if (response.status === 200) {
      yield put(
        TerminalBlocksActions.toggleBlocksScheduleStatusSuccess({
          toggleBlockScheduleStatus: response.data,
        }),
      );

      yield put(
        TerminalBlocksActions.listTerminalBlocksRequest({
          body: {
            id: blockId,
            _includes: 'schedule',
          },
        }),
      );

      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Bloqueio atualizado com sucesso',
        }),
      );
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('terminalBlocks - listTerminalBlocks - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(TerminalBlocksActions.toggleBlocksScheduleStatusFailure());
  }
}

function* toggleTerminalPanic({
  payload,
}: ReturnType<typeof TerminalBlocksActions.toggleTerminalPanicRequest>): any {
  try {
    const response = yield call(terminalBlocksApi.toggleTerminalPanic, {
      data: { ...payload },
    });

    if (response.status === 200) {
      yield put(
        TerminalBlocksActions.toggleTerminalPanicSuccess({
          toggleTerminalPanic: response.data,
        }),
      );

      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Bloqueios atualizados com sucesso',
        }),
      );

      yield window.location.reload();
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('terminalBlocks - terminalTogglePanic - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(TerminalBlocksActions.toggleTerminalPanicFailure());
  }
}

function* createSchedule({
  payload,
}: ReturnType<typeof TerminalBlocksActions.createBlockScheduleRequest>): any {
  try {
    yield put(
      DialogsActions.setOpenLoading({ title: 'Criando alteração programada' }),
    );

    const response = yield call(terminalBlocksApi.createSchedule, {
      data: { ...payload },
    });

    if (response.status === 200) {
      yield put(
        TerminalBlocksActions.createBlockScheduleSuccess({
          terminalBlockScheduleData: response.data,
        }),
      );
      yield put(DialogsActions.setClose());
      yield put(DialogsActions.setSecondClose());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Alteração programada criada com sucesso',
        }),
      );
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('terminalBlocks - createTerminalBlock - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(TerminalBlocksActions.createTerminalBlockFailure());
    yield put(DialogsActions.setClose());
  }
}

function* updateSchedule({
  payload,
}: ReturnType<typeof TerminalBlocksActions.updateBlockScheduleRequest>): any {
  try {
    yield put(
      DialogsActions.setOpenLoading({ title: 'Atualizando agendamentos' }),
    );

    const response = yield call(terminalBlocksApi.updateSchedule, {
      data: { ...payload },
    });

    if (response.status === 200) {
      yield put(
        TerminalBlocksActions.updateBlockScheduleSuccess({
          terminalBlockScheduleData: response.data,
        }),
      );
      yield put(DialogsActions.setClose());
      yield put(DialogsActions.setSecondClose());

      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Agendamento atualizado com sucesso',
        }),
      );
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('terminalBlocks - updateTerminalBlock - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(TerminalBlocksActions.updateTerminalBlockFailure());
    yield put(DialogsActions.setClose());
  }
}

function* removeSchedule({
  payload,
}: ReturnType<typeof TerminalBlocksActions.deleteBlockScheduleRequest>): any {
  try {
    yield put(
      DialogsActions.setOpenLoading({ title: 'Excluindo agendamento' }),
    );

    const response = yield call(terminalBlocksApi.deleteSchedule, {
      params: { ...payload },
    });

    if (response.status === 200) {
      yield put(TerminalBlocksActions.deleteTerminalBlockSuccess());
      yield put(DialogsActions.setClose());
      yield put(DialogsActions.setSecondClose());

      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Agendamento excluído com sucesso',
        }),
      );
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('terminalBlocks - deleteTerminalBlock - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(TerminalBlocksActions.deleteTerminalBlockFailure());
    yield put(DialogsActions.setClose());
  }
}

function* scheduleGateCreate({
  payload,
}: ReturnType<
  typeof TerminalBlocksActions.blockScheduleGateCreateRequest
>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Alterando status' }));

    const response = yield call(terminalBlocksApi.scheduleGateCreate, {
      data: { ...payload },
    });

    if (response.status === 200) {
      yield put(
        TerminalBlocksActions.blockScheduleGateCreateSuccess({
          terminalBlockScheduleData: response.data,
        }),
      );

      yield put(DialogsActions.setClose());

      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Alteração programada criada com sucesso',
        }),
      );
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('terminalBlocks - createTerminalBlock - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(TerminalBlocksActions.blockScheduleGateCreateFailure());
    yield put(DialogsActions.setClose());
  }
}

function* get({
  payload,
}: ReturnType<typeof TerminalBlocksActions.getTerminalBlockRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Carregando bloqueio' }));
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(terminalBlocksApi.get, {
      params: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        TerminalBlocksActions.getTerminalBlockSuccess({
          terminalBlockData: response.data,
        }),
      );
      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('terminalBlocks - getTerminalBlock - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(TerminalBlocksActions.getTerminalBlockFailure());
    yield put(DialogsActions.setClose());
  }
}

function* filter({
  payload,
}: ReturnType<typeof TerminalBlocksActions.filterTerminalBlocksRequest>): any {
  try {
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(terminalBlocksApi.get, {
      params: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        TerminalBlocksActions.filterTerminalBlocksSuccess({
          terminalBlocksList: response.data,
          totalCount: parseInt(response.headers.totalcount, 10),
        }),
      );
      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('terminalBlocks - filterTerminalBlocks - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(TerminalBlocksActions.filterTerminalBlocksFailure());
    yield put(DialogsActions.setClose());
  }
}

function* create({
  payload,
}: ReturnType<typeof TerminalBlocksActions.createTerminalBlockRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Criando bloqueio' }));

    const response = yield call(terminalBlocksApi.create, {
      data: { ...payload },
    });

    if (response.status === 200) {
      yield put(
        TerminalBlocksActions.createTerminalBlockSuccess({
          terminalBlockData: response.data,
        }),
      );
      yield put(DialogsActions.setClose());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Bloqueio criado com sucesso',
        }),
      );
      yield put(
        TerminalBlocksActions.listTerminalBlocksRequest({
          body: {
            _includes: 'terminal,line',
          },
        }),
      );
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('terminalBlocks - createTerminalBlock - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(TerminalBlocksActions.createTerminalBlockFailure());
    yield put(DialogsActions.setClose());
  }
}

function* update({
  payload,
}: ReturnType<typeof TerminalBlocksActions.updateTerminalBlockRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Atualizando bloqueio' }));

    const response = yield call(terminalBlocksApi.update, {
      data: { ...payload },
    });

    if (response.status === 200) {
      yield put(
        TerminalBlocksActions.updateTerminalBlockSuccess({
          terminalBlockData: response.data,
        }),
      );
      yield put(DialogsActions.setClose());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Bloqueio atualizado com sucesso',
        }),
      );
      yield put(
        TerminalBlocksActions.listTerminalBlocksRequest({
          body: {
            _includes: 'terminal,line',
          },
        }),
      );
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('terminalBlocks - updateTerminalBlock - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(TerminalBlocksActions.updateTerminalBlockFailure());
    yield put(DialogsActions.setClose());
  }
}

function* remove({
  payload,
}: ReturnType<typeof TerminalBlocksActions.deleteTerminalBlockRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Excluindo bloqueio' }));

    const response = yield call(terminalBlocksApi.delete, {
      params: { ...payload },
    });

    if (response.status === 200) {
      yield put(TerminalBlocksActions.deleteTerminalBlockSuccess());
      yield put(DialogsActions.setClose());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Bloqueio excluído com sucesso',
        }),
      );
      yield put(
        TerminalBlocksActions.listTerminalBlocksRequest({
          body: {
            _includes: 'terminal,line',
          },
        }),
      );
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('terminalBlocks - deleteTerminalBlock - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(TerminalBlocksActions.deleteTerminalBlockFailure());
    yield put(DialogsActions.setClose());
  }
}

// eslint-disable-next-line import/prefer-default-export
export const terminalBlocksSagas = [
  takeLatest(TerminalBlocksTypes.LIST_TERMINAL_BLOCKS_REQUEST, list),
  takeLatest(TerminalBlocksTypes.TOGGLE_BLOCK_SCHEDULE_STATUS_REQUEST, toggle),
  takeLatest(
    TerminalBlocksTypes.TOGGLE_TERMINAL_PANIC_REQUEST,
    toggleTerminalPanic,
  ),
  takeLatest(TerminalBlocksTypes.GET_TERMINAL_BLOCK_REQUEST, get),
  takeLatest(TerminalBlocksTypes.FILTER_TERMINAL_BLOCKS_REQUEST, filter),
  takeLatest(TerminalBlocksTypes.CREATE_TERMINAL_BLOCK_REQUEST, create),
  takeLatest(TerminalBlocksTypes.CREATE_BLOCK_SCHEDULE_REQUEST, createSchedule),
  takeLatest(TerminalBlocksTypes.UPDATE_BLOCK_SCHEDULE_REQUEST, updateSchedule),
  takeLatest(
    TerminalBlocksTypes.BLOCK_SCHEDULE_GATE_CREATE_REQUEST,
    scheduleGateCreate,
  ),
  takeLatest(TerminalBlocksTypes.UPDATE_TERMINAL_BLOCK_REQUEST, update),
  takeLatest(TerminalBlocksTypes.DELETE_TERMINAL_BLOCK_REQUEST, remove),
  takeLatest(TerminalBlocksTypes.DELETE_BLOCK_SCHEDULE_REQUEST, removeSchedule),
];
